import React from 'react';
import { ArrowRight, Truck, CheckCircle, Clock, MapPin, Shield, Package, Home } from 'lucide-react';
import SEO from '../components/SEO';

const MovingStorage = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SelfStorage',
    name: 'Interskate Self Storage – Moving Storage in Lewisville, TX',
    url: 'https://www.interskateselfstorage.com/moving-storage-lewisville',
    telephone: '(469) 251-6255',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '1303 S Stemmons Fwy',
      addressLocality: 'Lewisville',
      addressRegion: 'TX',
      postalCode: '75067',
      addressCountry: 'US'
    },
    description: 'Moving storage in Lewisville, TX. Drive-up units on the I-35E service road — perfect for staging a move, between-home storage, and downsizing.',
    priceRange: '$'
  };

  const movingScenarios = [
    {
      icon: <Home className="h-10 w-10 text-green-600" />,
      title: 'Between Homes',
      description: 'Your old house sold before the new one closes. Store everything safely while you bridge the gap — no rush, no stress.'
    },
    {
      icon: <Truck className="h-10 w-10 text-green-600" />,
      title: 'Staging Your Home to Sell',
      description: "Declutter and stage your home for a faster, higher sale. Store the extra furniture here while your home is on the market."
    },
    {
      icon: <Package className="h-10 w-10 text-green-600" />,
      title: 'Downsizing',
      description: 'Moving to a smaller place? Keep items you love but can\'t fit yet — decide later what stays and what goes.'
    },
    {
      icon: <MapPin className="h-10 w-10 text-green-600" />,
      title: 'Relocating to Lewisville',
      description: 'Moving to the DFW area? Store your belongings here while you get settled — right off I-35E for easy access.'
    }
  ];

  const benefits = [
    'Drive-up access — U-Haul and moving truck friendly',
    'Wide drive aisles for 26-foot trucks and cargo vans',
    'Right on I-35E service road — easy in, easy out',
    'Month-to-month rental — no long-term commitment',
    'Electronic gate access 6 AM – 10 PM daily',
    '24/7 video surveillance',
    'Online rentals — move in without an office visit',
    'Multiple unit sizes to fit any move'
  ];

  const unitSizes = [
    { size: '5x10', sqft: '50 sq ft', ideal: 'Small apartment, boxes, small furniture' },
    { size: '10x10', sqft: '100 sq ft', ideal: '1–2 bedroom apartment contents' },
    { size: '10x15', sqft: '150 sq ft', ideal: '2–3 bedroom home' },
    { size: '10x20', sqft: '200 sq ft', ideal: '3–4 bedroom home or full household move' },
    { size: '10x30', sqft: '300 sq ft', ideal: 'Large home or combined household move' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Moving Storage in Lewisville, TX | Interskate Self Storage | I-35E"
        description="Moving storage in Lewisville, TX on the I-35E service road. Drive-up units perfect for staging a move, between-home storage, and downsizing. Month-to-month. Rent online."
        canonical="/moving-storage-lewisville"
        structuredData={structuredData}
      />

      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="moving-storage-title">
            Moving Storage in Lewisville, TX
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Drive-up storage right on I-35E — the easiest place to store during a move in the DFW area
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Moving is stressful enough. Interskate Self Storage makes the storage part easy — with drive-up units on the I-35E service road in Lewisville that are designed for moving trucks. Pull your U-Haul straight to your unit, unload, and you're done. No elevators, no stairs, no long halls.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Whether you need storage for a week or several months, our month-to-month leases keep things flexible so you can focus on the move, not the fine print.
          </p>
        </div>
      </section>

      {/* Moving Scenarios */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Moving Storage Solutions for Every Situation
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {movingScenarios.map((scenario, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md text-center">
                <div className="flex justify-center mb-4">{scenario.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{scenario.title}</h3>
                <p className="text-gray-600">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unit Sizes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pick the Right Size for Your Move
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              All units are ground-level drive-up — moving truck friendly
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {unitSizes.map((unit, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl text-center border border-gray-200">
                <div className="text-2xl font-bold text-green-600 mb-1">{unit.size}</div>
                <div className="text-sm text-gray-500 mb-3">{unit.sqft}</div>
                <p className="text-gray-700 text-sm">{unit.ideal}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Lewisville Movers Choose Interskate
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Truck className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Moving Truck Friendly</h3>
              <p className="text-gray-600">Wide drive aisles designed for U-Hauls, Penske trucks, and cargo vans up to 26 feet.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Unbeatable Location</h3>
              <p className="text-gray-600">Right on the I-35E service road in Lewisville — accessible from the highway without fighting traffic lights.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Clock className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Extended Access Hours</h3>
              <p className="text-gray-600">Gate open 6 AM to 10 PM every day — get in early or late to fit your moving schedule.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits List */}
      <section className="py-16 bg-green-50 border-t border-b border-green-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Everything You Need for a Smooth Move
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Reserve Your Unit Before Moving Day</h2>
          <p className="text-xl text-green-100 mb-8">
            Rent online in 2 minutes — no office visit, no paperwork.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://interskate.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="rent-now-button"
            >
              Rent Online Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-green-700 transition-colors"
              data-testid="contact-button"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MovingStorage;
